import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, FormControlProps, FormHelperText, SelectChangeEvent } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { SelectProps } from '@mui/material/Select/Select';
import { AccessEnum } from '@shared/types';
import OverflowTooltip from '@shared/components/ui/OverflowTooltip';
import AccessChip from '@shared/components/others/AccessChip/AccessChip';

interface AccessSelectorProps extends FormControlProps {
    data: AccessEnum | null | ' ';
    setData: (newAccess: AccessEnum) => void;
    objectType?: 'scheme' | 'stand';
    withHint?: boolean;
    SelectProps?: SelectProps<AccessEnum>;
}

const AccessSelector: FC<AccessSelectorProps> = ({ data, setData, objectType, withHint, SelectProps, ...props }) => {
    const { t } = useTranslation();

    const handleChangeAccess = (event: SelectChangeEvent<typeof data>) => {
        const {
            target: { value },
        } = event;
        setData(value as AccessEnum);
    };

    const helperText = useMemo(() => {
        if (!data || data === ' ') {
            return '';
        }

        return objectType
            ? {
                  Public: t(`public_${objectType}_hint`),
                  Locked: t(`locked_${objectType}_hint`),
                  Private: t(`private_${objectType}_hint`),
              }[data]
            : t(`${data.toLowerCase()}_object_hint`);
    }, [data, objectType, t]);

    return (
        <FormControl size="small" {...props}>
            <InputLabel id="access-chip-label" shrink>
                {t('access')}
            </InputLabel>
            <Select
                {...SelectProps}
                sx={{ width: 170, height: 42, ...SelectProps?.sx }}
                labelId="access-chip-label"
                value={data ?? ''}
                onChange={handleChangeAccess}
                label={t('access')}
                displayEmpty
                renderValue={(selected) =>
                    selected ? (
                        <OverflowTooltip title={selected}>
                            <AccessChip access={selected as AccessEnum} />
                        </OverflowTooltip>
                    ) : (
                        <Box sx={{ color: 'var(--text-disabled)' }}>{`${t('any_access')}...`}</Box>
                    )
                }
            >
                {Object.values(AccessEnum).map((accessType) => (
                    <MenuItem key={accessType} value={accessType}>
                        <AccessChip access={accessType} />
                    </MenuItem>
                ))}
            </Select>
            {withHint && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
    );
};

export default AccessSelector;
